<template>
    <a-table
        size="small"
        :columns="columns"
        :data-source="sources"
        row-key="id"
        :loading="loading"
        :pagination="false"
        :scroll="{ x: 'max-content' }"
        :expandIcon="renderExpandIcon"
        :locale="{ emptyText: 'Nenhum fornecedor vinculado a este produto.' }"
    >
        <template slot="provider" slot-scope="text, source">
            <div class="psp-provider">
                <a-tooltip v-if="source.is_preferred" title="Fornecedor preferencial">
                    <i class="fa fa-star psp-star"></i>
                </a-tooltip>
                <div>
                    <div class="psp-strong">{{ source.provider ? source.provider.name : "--" }}</div>
                    <div class="psp-muted" v-if="source.provider && source.provider.cnpj">{{ source.provider.cnpj }}</div>
                </div>
            </div>
        </template>
        <template slot="regime" slot-scope="text">
            <a-tag :color="regimeColor(text)">{{ regimeLabel(text) }}</a-tag>
        </template>
        <template slot="stores" slot-scope="text, source">
            {{ source.all_stores ? "Todas" : `${(source.stores || []).length} loja(s)` }}
        </template>
        <template slot="packing" slot-scope="text, source">
            {{ source.packing_unit ? `${source.packing_unit} ${source.packing_quantity || ""}`.trim() : "--" }}
        </template>
        <template slot="number" slot-scope="text">{{ text != null ? text : "--" }}</template>
        <template slot="leadTime" slot-scope="text">{{ text != null ? `${text} dia(s)` : "--" }}</template>
        <template slot="agreedPrice" slot-scope="text, source">
            <template v-if="source.agreed_price != null">
                <div class="psp-money">{{ source.agreed_price | currency }}</div>
                <div class="psp-muted" v-if="source.agreed_price_valid_until">até {{ formatDate(source.agreed_price_valid_until) }}</div>
            </template>
            <span v-else>--</span>
        </template>
        <template slot="text" slot-scope="text">{{ text || "--" }}</template>
        <template slot="lastPurchase" slot-scope="text, source">
            <template v-if="source.last_purchase_at">
                <div class="psp-money">{{ source.last_purchase_price | currency }} · {{ source.last_purchase_quantity }}</div>
                <div class="psp-muted">{{ formatDate(source.last_purchase_at) }}</div>
            </template>
            <span v-else>--</span>
        </template>
        <template slot="lastOffer" slot-scope="text, source">
            <template v-if="source.last_offer_at">
                <div class="psp-money">{{ source.last_offer_price | currency }}</div>
                <div class="psp-muted">{{ formatDate(source.last_offer_at) }}</div>
            </template>
            <span v-else>--</span>
        </template>
        <template slot="source" slot-scope="text">
            <a-tag>{{ sourceLabel(text) }}</a-tag>
        </template>
        <template slot="active" slot-scope="text">
            <a-tag :color="text ? 'green' : 'default'">{{ text ? "Ativo" : "Inativo" }}</a-tag>
        </template>

        <div slot="expandedRowRender" slot-scope="source" class="psp-expanded">
            <a-table
                size="small"
                :columns="storeColumns"
                :data-source="source.stores"
                row-key="store_id"
                :pagination="false"
            >
                <template slot="store" slot-scope="text">{{ storeName(text) }}</template>
                <template slot="regime" slot-scope="text">
                    <a-tag :color="regimeColor(text)">{{ regimeLabel(text) }}</a-tag>
                </template>
                <template slot="lastPurchase" slot-scope="text, store">
                    <template v-if="store.last_purchase_at">
                        {{ store.last_purchase_price | currency }} · {{ store.last_purchase_quantity }} · {{ formatDate(store.last_purchase_at) }}
                    </template>
                    <span v-else>--</span>
                </template>
                <template slot="source" slot-scope="text">
                    <a-tag>{{ sourceLabel(text) }}</a-tag>
                </template>
                <template slot="active" slot-scope="text">
                    <a-tag :color="text ? 'green' : 'default'">{{ text ? "Ativo" : "Inativo" }}</a-tag>
                </template>
            </a-table>
        </div>
    </a-table>
</template>

<script>
import moment from "moment";

const REGIMES = { QUOTATION: "Cotação", DIRECT: "Pedido direto" };
const SOURCES = { MANUAL: "Manual", HISTORY: "Histórico", INTELLIGENCE: "Intelligence" };

export default {
    name: "ProductSuppliers",
    props: {
        sources: { type: Array, default: () => [] },
        stores: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
    },
    data() {
        return {
            columns: [
                { title: "Fornecedor", key: "provider", scopedSlots: { customRender: "provider" } },
                { title: "Regime", dataIndex: "purchase_regime", key: "purchase_regime", align: "center", scopedSlots: { customRender: "regime" } },
                { title: "Lojas", key: "stores", align: "center", scopedSlots: { customRender: "stores" } },
                { title: "Embalagem", key: "packing", align: "center", scopedSlots: { customRender: "packing" } },
                { title: "Pedido mín.", dataIndex: "min_order_quantity", key: "min_order_quantity", align: "center", scopedSlots: { customRender: "number" } },
                { title: "Prazo", dataIndex: "lead_time_days", key: "lead_time_days", align: "center", scopedSlots: { customRender: "leadTime" } },
                { title: "Preço acordado", key: "agreed_price", align: "right", scopedSlots: { customRender: "agreedPrice" } },
                { title: "Pagamento", dataIndex: "payment_terms", key: "payment_terms", scopedSlots: { customRender: "text" } },
                { title: "Última compra", key: "last_purchase", align: "right", scopedSlots: { customRender: "lastPurchase" } },
                { title: "Última oferta", key: "last_offer", align: "right", scopedSlots: { customRender: "lastOffer" } },
                { title: "Compras", dataIndex: "purchase_count", key: "purchase_count", align: "center" },
                { title: "Origem", dataIndex: "source", key: "source", align: "center", scopedSlots: { customRender: "source" } },
                { title: "Situação", dataIndex: "is_active", key: "is_active", align: "center", scopedSlots: { customRender: "active" } },
            ],
            storeColumns: [
                { title: "Loja", dataIndex: "store_id", key: "store_id", scopedSlots: { customRender: "store" } },
                { title: "Regime", dataIndex: "effective_regime", key: "effective_regime", align: "center", scopedSlots: { customRender: "regime" } },
                { title: "Última compra", key: "last_purchase", scopedSlots: { customRender: "lastPurchase" } },
                { title: "Origem", dataIndex: "source", key: "source", align: "center", scopedSlots: { customRender: "source" } },
                { title: "Situação", dataIndex: "is_active", key: "is_active", align: "center", scopedSlots: { customRender: "active" } },
            ],
        };
    },
    methods: {
        formatDate(value) {
            return value ? moment(value).format("DD/MM/YYYY") : "--";
        },
        regimeLabel(value) {
            return REGIMES[value] || value || "--";
        },
        regimeColor(value) {
            return value === "DIRECT" ? "orange" : "blue";
        },
        sourceLabel(value) {
            return SOURCES[value] || value || "--";
        },
        storeName(storeId) {
            const store = this.stores.find((item) => String(item.loj_id) === String(storeId));
            return store ? store.loj_descricao : `#${storeId}`;
        },
        renderExpandIcon({ record, expanded, onExpand }) {
            const h = this.$createElement;
            if (!record.stores || !record.stores.length) return h("span");
            return h("a-icon", {
                class: "row-icon",
                attrs: { title: expanded ? "Esconder lojas" : "Ver lojas" },
                props: { type: expanded ? "minus-square" : "plus-square" },
                on: { click: (e) => onExpand(record, e) },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.psp-provider {
    display: flex;
    align-items: center;
    gap: 8px;
}
.psp-star {
    color: #fadb14;
}
.psp-strong {
    font-weight: 600;
    color: #262626;
    text-transform: uppercase;
}
.psp-muted {
    font-size: 12px;
    color: #8c8c8c;
}
.psp-money {
    white-space: nowrap;
}
.psp-expanded {
    padding: 4px 0 8px 0;
}
</style>
